import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import staticURL from 'lib/staticURL';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import {BLUE_LIGHT, GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface Props {
  items: Array<{description: string; imageSrc: string}>;
  imageWidth: number;
  imageHeight: number;
}

export default function Carousel(props: Props): JSX.Element {
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const maxIndex = props.items.length - 1;
  const adjustedImageWidth = props.imageWidth + 8; // Account for box shadow
  return (
    <div
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
      className={css(styles.container)}
      style={{height: props.imageHeight + 48, width: adjustedImageWidth}}
    >
      {isHovered && (
        <BenchlingIcon
          icon="chevron-left"
          className={css(styles.navArrows)}
          style={{left: 10}}
          onClick={() => {
            setVisibleIndex((oldIndex) => (oldIndex === 0 ? maxIndex : oldIndex - 1));
          }}
        />
      )}
      <ol className={css(styles.slides)}>
        {props.items.map((item, i) => (
          <li
            className={css(styles.slide)}
            key={i}
            style={{left: (i - visibleIndex) * adjustedImageWidth, width: adjustedImageWidth}}
          >
            <span className={css(styles.description)}>{item.description}</span>
            <img
              className={css(styles.image)}
              width={props.imageWidth}
              height={props.imageHeight}
              src={staticURL(item.imageSrc)}
            />
          </li>
        ))}
      </ol>
      {isHovered && (
        <BenchlingIcon
          icon="chevron-right"
          className={css(styles.navArrows)}
          style={{right: 10}}
          onClick={() => {
            setVisibleIndex((oldIndex) => (oldIndex === maxIndex ? 0 : oldIndex + 1));
          }}
        />
      )}
      <div className="u-flex-display">
        {props.items.map((_, i) => (
          <div
            key={i}
            className={css(styles.dot, i === visibleIndex && styles.activeDot)}
            onClick={() => {
              setVisibleIndex(i);
            }}
          />
        ))}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  slides: {
    position: 'relative',
    display: 'flex',
    overflow: 'hidden',
    listStyle: 'none',
    width: '100%',
    height: '100%',
  },
  slide: {
    position: 'absolute',
    transition: 'left 0.5s',
  },
  navArrows: {
    position: 'absolute',
    zIndex: 5,
    color: 'white',
    backgroundColor: 'black',
    opacity: 0.7,
    borderRadius: '10px',
    cursor: 'pointer',
  },
  image: {
    boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)',
    margin: '4px',
  },
  dot: {
    width: '7px',
    height: '7px',
    backgroundColor: GRAY_DARKER,
    borderRadius: '5px',
    margin: '2px',
    cursor: 'pointer',
  },
  activeDot: {
    backgroundColor: BLUE_LIGHT,
  },
  description: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARKER,
    paddingLeft: '5px',
    letterSpacing: '-.01em',
  },
});
